// File: frontend/src/components/seo-analytics/AhrefsBacklinksTab.jsx
// Version: 1.2
// Date: 2026-04-28
// Description: Backlinks Trend tab — recharts LineChart of backlinks +
//              ref_domains over user-selectable date range (7d / 30d / 90d
//              / 1y). Uses dual Y-axes since backlinks (millions) and
//              ref_domains (thousands) live on very different scales.
// History:
// - [v1.2]: Added "1y" (365 day) range pill to surface backfilled monthly
//           snapshots populated by Phase 07-04 backfill_history.
// - [v1.1]: Light editorial restyle — white card, pill-style range
//           selector, polished chart container.
// - [v1.0]: Initial dark theme implementation.

import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import seoAnalyticsService from '../../services/seoAnalyticsService';

const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return iso;
  }
};

const formatFull = (n) => (n == null ? '—' : Number(n).toLocaleString());

const AhrefsBacklinksTab = ({ domainId }) => {
  const [days, setDays] = useState(30);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrends = useCallback(
    async (rangeDays) => {
      if (!domainId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await seoAnalyticsService.getAhrefsTrends(domainId, rangeDays);
        setTrends(data || null);
      } catch (err) {
        console.error('[AhrefsBacklinksTab] fetch failed:', err);
        setError(err.message || 'Failed to load backlinks trend.');
      } finally {
        setLoading(false);
      }
    },
    [domainId]
  );

  useEffect(() => {
    fetchTrends(days);
  }, [fetchTrends, days]);

  const points = (trends?.data_points || [])
    .slice()
    .sort((a, b) => new Date(a.snapshot_date) - new Date(b.snapshot_date));

  const chartData = points.map((p) => ({
    date: formatShortDate(p.snapshot_date),
    fullDate: p.snapshot_date,
    backlinks: p.backlinks,
    ref_domains: p.ref_domains,
  }));

  const recentRows = points.slice(-5).reverse();

  const rangePillStyle = (isActive) => ({
    padding: '0.375rem 0.75rem',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
    border: 'none',
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    color: isActive ? '#0f172a' : '#64748b',
    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
    opacity: loading && !isActive ? 0.6 : 1,
    transition: 'background 0.15s, color 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
          Backlinks &amp; Referring Domains
        </h3>
        <div
          role="group"
          aria-label="Date range"
          style={{
            display: 'inline-flex',
            padding: '4px',
            backgroundColor: '#f1f5f9',
            borderRadius: '0.5rem',
            gap: '2px',
          }}
        >
          {[
            { value: 7, label: '7d' },
            { value: 30, label: '30d' },
            { value: 90, label: '90d' },
            { value: 365, label: '1y' },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              disabled={loading}
              aria-pressed={days === value}
              aria-label={`Show last ${value} days`}
              style={rangePillStyle(days === value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="chart-loading">Loading backlinks trend…</div>
      ) : error ? (
        <div className="inline-error">{error}</div>
      ) : !chartData.length ? (
        <div className="empty-state">
          Daily snapshots in progress. First trend visible after 2+ days of data.
        </div>
      ) : (
        <>
          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1rem',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
            }}
          >
            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 30, left: 12, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                  }
                  label={{
                    value: 'Backlinks',
                    angle: -90,
                    position: 'insideLeft',
                    fontSize: 11,
                    fill: '#2563eb',
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
                  }
                  label={{
                    value: 'Ref Domains',
                    angle: 90,
                    position: 'insideRight',
                    fontSize: 11,
                    fill: '#d97706',
                  }}
                />
                <Tooltip
                  formatter={(val, name) => [formatFull(val), name === 'backlinks' ? 'Backlinks' : 'Ref Domains']}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="backlinks"
                  name="Backlinks"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="ref_domains"
                  name="Ref Domains"
                  stroke="#d97706"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1rem 1.25rem',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)',
            }}
          >
            <h4
              style={{
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#64748b',
                margin: '0 0 0.5rem 0',
              }}
            >
              Recent snapshots
            </h4>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Backlinks</th>
                    <th>Ref Domains</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRows.map((p) => (
                    <tr key={p.snapshot_date}>
                      <td>{p.snapshot_date}</td>
                      <td>{formatFull(p.backlinks)}</td>
                      <td>{formatFull(p.ref_domains)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AhrefsBacklinksTab;
